import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Fab from "@mui/material/Fab";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Backdrop, CircularProgress } from "@mui/material";
import { format } from "date-fns";
import React, { FC, useEffect, useState, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { saveAs } from "file-saver";
import { utils, write } from "xlsx";
import AgencyScansTable from "../../components/AgencyScansTable";
import DashboardLayout from "../../components/Common/DashboardLayout";
import useAuthGuard from "../../Hooks/useAuthGuard";
import useAuthUserData from "../../Hooks/useAuthUserData";
import { useAgencyScans } from "../../Hooks/useAgencyScans";
import { getAgencyScansForExport } from "../../Services/scansService";
import { FR_ROLE } from "../../utils/constants";

interface Scan {
  id: string;
  createdAt: string;
  updatedAt?: string;
  type: string;
  method: string;
  subscriber: {
    firstName: string;
    lastName: string;
  };
  firstresponder: {
    firstName: string;
    lastName: string;
  };
}

interface FilteringInterface {
  order: {
    by: string;
    type: string;
  };
  filter: {
    column: string | null;
    operator: string;
    value: string | null;
  };
  or?: {
    column: string;
    operator: string;
    value: string;
  };
}

const AgencyScanHistory: FC = () => {
  useAuthGuard({
    allowedRoles: [
      FR_ROLE.FIRST_RESPONDER_ADMIN,
      FR_ROLE.FIRST_RESPONDER_OWNER,
    ],
  });

  const { user } = useAuthUserData();
  const agencyId = user?.agency;

  // Only allow FIRST_RESPONDER_OWNER and FIRST_RESPONDER_ADMIN
  const hasAccess =
    user?.rol === FR_ROLE.FIRST_RESPONDER_OWNER ||
    user?.rol === FR_ROLE.FIRST_RESPONDER_ADMIN;

  const [searchInput, setSearchInput] = useState("");
  const [date, setDate] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({ startDate: null, endDate: null });
  const [showDateRange, setShowDateRange] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");
  const [loader, setLoader] = useState<{
    message: string | null;
    status: boolean;
  }>({
    message: null,
    status: false,
  });
  const [filtering, setFiltering] = useState<FilteringInterface>({
    order: {
      by: "createdAt",
      type: "desc",
    },
    filter: {
      column: null,
      operator: "contains",
      value: null,
    },
  });

  const { scansData, loading, error, refetchScans } = useAgencyScans(
    hasAccess ? agencyId : undefined
  );
  const refetchScansRef = useRef(refetchScans);

  // Keep ref updated with latest refetchScans function
  useEffect(() => {
    refetchScansRef.current = refetchScans;
  }, [refetchScans]);

  // Debounce search input and refetch data when filters change
  useEffect(() => {
    const timer = setTimeout(
      () => {
        if (agencyId && hasAccess) {
          // Convert column filters to search parameters
          let combinedSearch = searchInput || "";

          // Add column filter search terms
          if (filtering.filter?.value && filtering.filter?.column) {
            const filterValue = filtering.filter.value as string;
            if (
              filtering.filter.column === "subscriber.firstName" ||
              filtering.filter.column === "firstresponder.firstName"
            ) {
              // For name filters, add to the general search
              combinedSearch = combinedSearch
                ? `${combinedSearch} ${filterValue}`
                : filterValue;
            }
          }

          const query = {
            page: currentPage,
            limit: pageSize,
            search: combinedSearch || undefined,
            startDate: date.startDate
              ? format(date.startDate, "yyyy-MM-dd")
              : undefined,
            endDate: date.endDate
              ? format(date.endDate, "yyyy-MM-dd")
              : undefined,
            sortBy,
            sortOrder,
            filtering,
          };
          refetchScansRef.current(query);
        }
      },
      searchInput ? 500 : 0
    ); // Debounce search, but not pagination/date changes

    return () => clearTimeout(timer);
  }, [
    searchInput,
    date,
    currentPage,
    pageSize,
    sortBy,
    sortOrder,
    filtering.filter?.column,
    filtering.filter?.value,
    filtering.filter?.operator,
    agencyId,
    hasAccess,
  ]);

  const handleSortChange = (
    newSortBy: string,
    newSortOrder: "ASC" | "DESC"
  ) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handleFilterPageChange = (page: number) => {
    setCurrentPage(page + 1); // Convert from 0-based to 1-based
  };

  const transformData = (data: Scan[]) => {
    return data.map((item) => {
      let formattedDate = "-";
      let formattedTime = "-";

      if (item.createdAt) {
        const date = new Date(item.createdAt);
        formattedDate = date.toLocaleDateString("en-US");
        formattedTime = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }

      const firstResponderName = item.firstresponder
        ? `${item.firstresponder.firstName || ""} ${
            item.firstresponder.lastName || ""
          }`
        : "";

      const subscriberName = `${item.subscriber.firstName} ${
        item.subscriber.lastName || ""
      }`;

      const pageViewed =
        item.type === "Family Members" ? "Members Page" : "Full Profile";

      const typeOfScan = (() => {
        const methods: any = {
          MMHID: "MMH ID",
          biometric: "Biometrics",
          QR: "QR",
        };
        return methods?.[item.method] ?? "N/A";
      })();

      return {
        "Date of Scan": formattedDate,
        "Time of Scan": formattedTime,
        "First Responder Name": firstResponderName,
        "Subscriber Name": subscriberName,
        "Page Viewed": pageViewed,
        "Type of Scan": typeOfScan,
      };
    });
  };

  const exportToExcel = async () => {
    if (!agencyId || !hasAccess) return;

    setLoader({ status: true, message: "Fetching results..." });
    try {
      const query = {
        search: searchInput || undefined,
        startDate: date.startDate
          ? format(date.startDate, "yyyy-MM-dd")
          : undefined,
        endDate: date.endDate ? format(date.endDate, "yyyy-MM-dd") : undefined,
        sortBy,
        sortOrder,
      };

      const resultSet = (await getAgencyScansForExport(
        agencyId,
        query
      )) as Scan[];
      const transformedData = transformData(resultSet);

      if (transformedData.length === 0) {
        setLoader({ status: false, message: null });
        return;
      }

      const worksheet = utils.json_to_sheet(transformedData);

      const headerStyling = Object.keys(transformedData[0]).map(
        (headerName: any) => {
          const max_width = transformedData.reduce(
            (w, r: any) => Math.max(w, r[headerName]?.length ?? 0),
            10
          );

          return { wch: Math.max(max_width, headerName.length) };
        }
      );

      worksheet["!cols"] = headerStyling;

      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, "Sheet1");

      const excelBuffer = write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });
      saveAs(
        blob,
        `MMH_AgencyScansExport_${format(new Date(), "MMddyyyy_hhmm")}.xlsx`
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoader({ status: false, message: null });
    }
  };

  const agencyName = user?.office || "Agency";

  if (!hasAccess) {
    return (
      <DashboardLayout page="agencyScanHistory">
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: "400px" }}
          gap="2rem"
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6">Loading page</Typography>
        </Grid>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout page="agencyScanHistory">
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          textAlign: "center",
        }}
        open={loader.status}
      >
        <CircularProgress color="inherit" />
        <Typography display="block">{loader.message}</Typography>
      </Backdrop>

      <Grid container direction="column" rowSpacing={3} alignItems="stretch">
        {/* Actions and filter */}
        <Grid item container direction="row" justifyContent="space-between">
          <Grid item>
            <Typography
              fontSize={20}
              fontWeight={600}
              sx={{
                color: "#404040",
              }}
            >
              {agencyName}
            </Typography>
          </Grid>
          <Grid item display="flex">
            <Grid item>
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: "4px",
                  height: "40px",
                  width: "280px",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "flex-end",
                  flexDirection: "row",
                  paddingLeft: "10px",
                }}
              >
                {showDateRange && (
                  <Box
                    sx={{
                      top: "10%",
                      position: "absolute",
                      zIndex: 1,
                    }}
                  >
                    <DatePicker
                      selected={date.startDate}
                      onChange={(dates) => {
                        const [start, end] = dates;
                        setDate({ startDate: start, endDate: end });
                      }}
                      startDate={date.startDate}
                      endDate={date.endDate}
                      selectsRange
                      inline
                    />
                  </Box>
                )}
                <Button
                  fullWidth
                  onClick={() => setShowDateRange(!showDateRange)}
                  variant="text"
                  sx={{
                    color: "#000000",
                    textAlign: "center",
                    textTransform: "capitalize",
                    display: "flex",
                    justifyContent: "space-around",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {!showDateRange && !date.startDate && !date.endDate ? (
                    <>
                      <Typography>Search by Scan date range</Typography>
                      <DateRangeOutlinedIcon color="inherit" />
                    </>
                  ) : (
                    <Stack
                      spacing={1}
                      direction="column"
                      sx={{
                        marginLeft: "120%",
                        position: "absolute",
                        zIndex: 1,
                      }}
                    >
                      {showDateRange && (
                        <Fab size="small" color="primary" aria-label="add">
                          <CloseOutlinedIcon />
                        </Fab>
                      )}
                      {showDateRange && date.startDate && date.endDate && (
                        <Fab
                          onClick={() =>
                            setDate({ startDate: null, endDate: null })
                          }
                          size="small"
                          color="primary"
                          aria-label="add"
                        >
                          <DeleteOutlineOutlinedIcon />
                        </Fab>
                      )}
                    </Stack>
                  )}
                  {date.startDate && date.endDate && (
                    <>
                      <Typography variant="subtitle2">
                        {`${format(date.startDate, "yyyy-MM-dd")} - ${format(
                          date.endDate,
                          "yyyy-MM-dd"
                        )}`}
                      </Typography>
                      <DateRangeOutlinedIcon color="inherit" />
                    </>
                  )}
                  {date.startDate && !date.endDate && (
                    <>
                      <Typography variant="subtitle2">
                        {`${format(
                          date.startDate,
                          "yyyy-MM-dd"
                        )} - select end date`}
                      </Typography>
                      <DateRangeOutlinedIcon color="inherit" />
                    </>
                  )}
                </Button>
              </Box>
            </Grid>
            <Grid item>
              <TextField
                placeholder="Search by Subscriber or FR Name"
                size="small"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                sx={{
                  width: "300px",
                  color: "black",
                  backgroundColor: "white",
                  mx: 2,
                  "& fieldset": { border: "none" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          color: "#000094",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ style: { color: "black" }, color: "black" }}
              />
            </Grid>
            <Grid item>
              <Grid
                item
                pt={2}
                justifyContent="center"
                alignItems="center"
                display="flex"
                columnGap={0.5}
                onClick={exportToExcel}
                title="Export to Excel"
                sx={{
                  cursor: "pointer",
                  userSelect: "none",
                  padding: 1,
                  "&:hover": {
                    background: "#F4F4F4",
                    borderRadius: 3,
                  },
                }}
              >
                <FileDownloadOutlinedIcon />
                <Typography>Export</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {/* Table */}
        <Grid item>
          <AgencyScansTable
            scans={scansData?.data || []}
            loading={loading}
            pagination={{
              page: currentPage,
              pageSize,
              total: scansData?.total || 0,
              onPageChange: setCurrentPage,
              onPageSizeChange: setPageSize,
            }}
            sorting={{
              sortBy,
              sortOrder,
              onSortChange: handleSortChange,
            }}
            filtering={{
              filters: filtering,
              setFiltering,
              changePage: handleFilterPageChange,
            }}
          />
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};

export default AgencyScanHistory;
